// tippi/src/pages/admin/invoices/AdminInvoiceList.js
import React, { useMemo, useState } from 'react';
import { invoiceApi } from '../../../firebase/functionsService';
import { downloadInvoicePdf } from '../../../utils/downloadInvoicePdf';
import { useAllDirectInvoices } from '../../../hooks/useAllDirectInvoices';
import InvoiceStatusChip from '../../../components/common/InvoiceStatusChip';
import { formatKr as kr } from '../../../utils/formatKr';
import { COLORS } from '../../../styles/tokens';

const CREDITABLE = ['issued', 'overdue', 'paid'];

const FILTERS = [
  { key: 'all',      label: 'Alle',       test: () => true },
  { key: 'unpaid',   label: 'Ubetalt',    test: i => i.kind === 'driver-to-orderer' && (i.status === 'issued' || i.status === 'overdue') },
  { key: 'overdue',  label: 'Forfalt',    test: i => i.kind === 'driver-to-orderer' && i.status === 'overdue' },
  { key: 'paid',     label: 'Betalt',     test: i => i.kind === 'driver-to-orderer' && i.status === 'paid' },
  { key: 'credited', label: 'Krediterte', test: i => i.kind === 'driver-to-orderer' && i.status === 'credited' },
];

const outstanding = i => (i.kind === 'credit-note'
  ? Number(i.grossAmount ?? 0)
  : Number(i.grossAmount ?? 0) - Number(i.creditedGross ?? 0));

const mailText = i => {
  if (i.mailStatus === 'sent') return 'E-post sendt';
  if (i.mailStatus === 'failed') return 'E-post feilet';
  if (i.mailStatus) return 'E-post venter';
  return '';
};

export default function AdminInvoiceList() {
  const { invoices, loading, error, truncated } = useAllDirectInvoices();
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null);

  const rows = useMemo(() => {
    const test = FILTERS.find(f => f.key === filter).test;
    const q = query.trim().toLowerCase();
    return invoices.filter(i => i.kind !== 'tippi-fee' && test(i) && (!q
      || `${i.invoiceNumber ?? ''} ${i.recipient?.name ?? ''} ${i.issuer?.name ?? ''}`.toLowerCase().includes(q)));
  }, [invoices, filter, query]);

  return (
    <>
      {error && <p role="alert" style={styles.error}>Kunne ikke hente fakturaer. Last siden på nytt.</p>}
      <div style={styles.filters}>
        {FILTERS.map(f => (
          <button key={f.key} type="button" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}
                  style={{ ...styles.filter, ...(filter === f.key ? styles.filterOn : {}) }}>
            {f.label}
          </button>
        ))}
      </div>
      <input aria-label="Søk i fakturaer" placeholder="Søk etter fakturanr., mottaker eller utsteder" value={query}
             onChange={e => setQuery(e.target.value)} style={styles.search} />

      {truncated && <p style={styles.hint}>Viser de siste 500 dokumentene.</p>}
      {loading && rows.length === 0 && <p style={styles.hint}>Laster fakturaer…</p>}

      {rows.map(i => (
        <InvoiceRow key={i.id} invoice={i} open={openId === i.id}
                    onToggle={() => setOpenId(openId === i.id ? null : i.id)} />
      ))}
      {!loading && rows.length === 0 && <p style={styles.hint}>Ingen fakturaer funnet.</p>}
    </>
  );
}

function InvoiceRow({ invoice: inv, open, onToggle }) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: 'ok' | 'warn' | 'error', text }
  const [crediting, setCrediting] = useState(false);
  const isNote = inv.kind === 'credit-note';
  const due = isNote ? null : inv.dueDate?.toDate?.();
  const canResend = isNote || inv.status === 'issued' || inv.status === 'overdue';
  const canCredit = inv.kind === 'driver-to-orderer' && CREDITABLE.includes(inv.status);
  const reminders = inv.reminderCount ?? 0;

  async function run(action, okText) {
    setNotice(null);
    setBusy(true);
    try {
      await action();
      if (okText) setNotice({ kind: 'ok', text: okText });
    } catch (err) {
      setNotice({ kind: 'error', text: err?.message || 'Noe gikk galt. Prøv igjen.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="admin-invoice-row" style={styles.row}>
      <div style={styles.rowTop}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={styles.name}>
            {inv.kind === 'credit-note' ? 'Kreditnota' : 'Faktura'} {inv.invoiceNumber}
          </div>
          <div style={styles.hint}>{inv.issuer?.name ?? '–'} → <strong>{inv.recipient?.name ?? '–'}</strong></div>
          <div style={styles.hint}>
            {kr(outstanding(inv))}{due ? ` · forfall ${due.toLocaleDateString('nb-NO')}` : ''}
            {mailText(inv) ? ` · ${mailText(inv)}` : ''}
            {reminders > 0 ? ` · ${reminders} ${reminders === 1 ? 'påminnelse' : 'påminnelser'}` : ''}
          </div>
        </div>
        <InvoiceStatusChip invoice={inv} />
        <button type="button" style={styles.smallBtn} aria-expanded={open} aria-controls={`invoice-actions-${inv.id}`}
                onClick={onToggle}>
          {open ? 'Lukk' : 'Handlinger'}
        </button>
      </div>
      {open && (
        <div id={`invoice-actions-${inv.id}`} style={styles.panel}>
          <div style={styles.actions}>
            <button type="button" style={styles.smallBtn} disabled={busy}
                    onClick={() => run(() => downloadInvoicePdf(inv.id))}>Last ned PDF</button>
            {canResend && (
              <button type="button" style={styles.smallBtn} disabled={busy}
                      onClick={() => run(() => invoiceApi.resendInvoiceMail(inv.id), 'E-posten er sendt på nytt.')}>Send på nytt</button>
            )}
            {canCredit && !crediting && (
              <button type="button" style={styles.danger} disabled={busy}
                      onClick={() => { setNotice(null); setCrediting(true); }}>Kreditér</button>
            )}
          </div>
          {crediting && (
            <CreditForm invoice={inv} onCancel={() => setCrediting(false)}
                        onDone={res => {
                          setCrediting(false);
                          setNotice(res?.deliveryError
                            ? { kind: 'warn', text: 'Kreditnotaen er opprettet, men e-posten feilet. Bruk «Send på nytt».' }
                            : { kind: 'ok', text: 'Kreditnotaen er opprettet.' });
                        }} />
          )}
          {notice && (
            <p role={notice.kind === 'error' ? 'alert' : 'status'}
               style={notice.kind === 'error' ? styles.error : notice.kind === 'warn' ? styles.warn : styles.okMsg}>
              {notice.text}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function CreditForm({ invoice: inv, onCancel, onDone }) {
  const remaining = Number(inv.netAmount ?? 0) - Number(inv.creditedNet ?? 0);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (busy) return;
    setError('');
    if (!reason.trim()) { setError('Skriv en begrunnelse'); return; }
    let netAmount;
    if (amount.trim() !== '') {
      const cleaned = amount.replace(/[\s\u00a0]/g, '');
      netAmount = /^\d+$/.test(cleaned) ? Number(cleaned) : NaN;
      if (!Number.isSafeInteger(netAmount) || netAmount <= 0) { setError('Beløpet må være et helt kronebeløp over 0 (bare hele kroner)'); return; }
      if (netAmount > remaining) { setError(`Beløpet er over gjenstående netto (${kr(remaining)})`); return; }
    }
    setBusy(true);
    try {
      const res = await invoiceApi.creditInvoice({
        invoiceId: inv.id, netAmount, reason: reason.trim(), expectedCreditNoteCount: inv.creditNoteCount ?? 0,
      });
      onDone(res);
    } catch (err) {
      setError(err?.message || 'Kunne ikke opprette kreditnota. Prøv igjen.');
      setBusy(false);
    }
  }

  return (
    <div style={styles.form}>
      <div style={styles.hint}>Gjenstående netto: {kr(remaining)}. La beløpet stå tomt for å kreditere hele resten.</div>
      <label style={styles.label} htmlFor={`credit-amount-${inv.id}`}>Beløp netto (kr, valgfritt)</label>
      <input id={`credit-amount-${inv.id}`} inputMode="numeric" value={amount}
             onChange={e => setAmount(e.target.value)} style={styles.input} />
      <label style={styles.label} htmlFor={`credit-reason-${inv.id}`}>Begrunnelse</label>
      <textarea id={`credit-reason-${inv.id}`} value={reason} onChange={e => setReason(e.target.value)} rows={2}
                maxLength={500} style={{ ...styles.input, height: 64, padding: 12 }} />
      {error && <p role="alert" style={styles.error}>{error}</p>}
      <div style={styles.actions}>
        <button type="button" style={styles.primary} disabled={busy} onClick={submit}>Opprett kreditnota</button>
        <button type="button" style={styles.smallBtn} disabled={busy} onClick={onCancel}>Avbryt</button>
      </div>
    </div>
  );
}

const styles = {
  filters:  { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  filter:   { border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textMid, borderRadius: 18, padding: '6px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  filterOn: { border: `1.5px solid ${COLORS.primary}`, color: COLORS.textDark, background: COLORS.surfaceHover },
  search:   { width: '100%', boxSizing: 'border-box', height: 44, padding: '0 14px', borderRadius: 10, border: `1px solid ${COLORS.border}`, fontSize: 14.5, marginBottom: 12, background: COLORS.white, color: COLORS.textDark },
  hint:     { fontSize: 12.5, color: COLORS.textMid, marginTop: 2 },
  row:      { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 8 },
  rowTop:   { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  name:     { fontWeight: 700, fontSize: 15, color: COLORS.textDark },
  panel:    { marginTop: 12, paddingTop: 12, borderTop: `1px solid ${COLORS.border}` },
  smallBtn: { border: `1.5px solid ${COLORS.primary}`, background: COLORS.white, color: COLORS.textDark, borderRadius: 18, padding: '6px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  form:     { marginTop: 12, display: 'flex', flexDirection: 'column' },
  label:    { fontSize: 12, fontWeight: 600, color: COLORS.textMid, margin: '8px 0 4px' },
  input:    { boxSizing: 'border-box', height: 42, padding: '0 12px', borderRadius: 10, border: `1px solid ${COLORS.border}`, fontSize: 14.5, fontFamily: 'inherit', background: COLORS.white, color: COLORS.textDark },
  error:    { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '8px 12px', borderRadius: 8, fontSize: 13.5, margin: '10px 0 0' },
  warn:     { background: COLORS.warningBg, color: COLORS.warningText, padding: '8px 12px', borderRadius: 8, fontSize: 13.5, margin: '10px 0 0' },
  okMsg:    { background: COLORS.successBg, color: COLORS.successText, padding: '8px 12px', borderRadius: 8, fontSize: 13.5, margin: '10px 0 0' },
  actions:  { display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' },
  primary:  { background: COLORS.accent, color: '#1a1a2e', border: 'none', borderRadius: 20, padding: '10px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  danger:   { background: COLORS.white, color: COLORS.danger, border: `1.5px solid ${COLORS.danger}`, borderRadius: 18, padding: '6px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
};
